/**
 * HOW WE WORKED IT OUT (Page 2) - Technical Details
 *
 * GROUND RULES:
 * - All technical detail lives here
 * - All assumptions explicitly stated
 * - Full ledger of calculations
 * - Confidence scoring
 * - Legal disclaimers
 *
 * This page protects the business by showing the maths clearly.
 */

import { DevOnly } from './DevOnly';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { LossesBreakdown } from '../types';
import { formatCurrency } from '../utils';

// ==================== TYPES ====================

export interface Year1Detail {
  spaceHeatKwh: number;
  dhwKwh: number;
  totalHeatKwh: number;
  lossesBreakdown?: LossesBreakdown;
  totalLossesKwh?: number;
  fuelInputKwh?: number;
  performance: {
    value: number;
    label: string;
    source: string;
  };
  fuelKwh: {
    gas?: number;
    electricity?: number;
  };
  costGbp: number;
  carbonKg: number;
  waterType?: string;
}

export interface HowWeWorkedItOutProps {
  recommendationState?: 'exploratory' | 'determinate';
  baseline: Year1Detail;
  option: {
    label: string;
    detail: Year1Detail;
  };
  assumptions: {
    gasTariffPPerKwh: number;
    electricityTariffPPerKwh: number;
    carbonFactorGas: number;
    carbonFactorElectricity: number;
    heatLossSource: string;
    performanceSource: string;
  };
  confidence: {
    level: 'high' | 'medium' | 'low';
    reason: string;
  };
  /**
   * Optional function to check if debug features should be enabled.
   * If not provided, thermodynamic assumptions will be hidden by default.
   */
  isDebugEnabled?: () => boolean;
}

// ==================== COMPONENT ====================

export function HowWeWorkedItOut({
  recommendationState,
  baseline,
  option,
  assumptions,
  confidence,
  isDebugEnabled = () => false, // Default to hidden unless explicitly enabled
}: HowWeWorkedItOutProps) {
  if (recommendationState === 'exploratory') {
    return (
      <div className="warning-box">
        <h3>Technical View Unavailable</h3>
        <p>Please enter your mains pressure and flow rate to view the technical breakdown.</p>
      </div>
    );
  }
  const optionIsCombiArchitecture =
    option.label.toLowerCase().includes('combi') ||
    option.detail.waterType === 'on_demand';

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatDecimal = (num: number, decimals: number = 2) => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Helper to format difference values with saving/increase labels
  const formatDifference = (difference: number): string => {
    if (difference > 0) {
      return `${formatNumber(difference)} saving`;
    } else if (difference < 0) {
      return `+${formatNumber(Math.abs(difference))} increase`;
    }
    return '—';
  };

  // Calculate input energy (energy used to produce heating)
  // For gas boilers: input = delivered / efficiency
  // For heat pumps: input = delivered / SPF
  const baselineInputSpace = baseline.spaceHeatKwh / baseline.performance.value;
  const baselineInputHotWater = baseline.dhwKwh / baseline.performance.value;
  const baselineInputTotal = baselineInputSpace + baselineInputHotWater;

  const optionInputSpace = option.detail.spaceHeatKwh / option.detail.performance.value;
  const optionInputHotWater = option.detail.dhwKwh / option.detail.performance.value;
  const optionInputTotal = optionInputSpace + optionInputHotWater;

  // Calculate differences for display
  const spaceDifference = baselineInputSpace - optionInputSpace;
  const hotWaterDifference = baselineInputHotWater - optionInputHotWater;
  const totalDifference = baselineInputTotal - optionInputTotal;

  return (
    <div className="how-we-worked-it-out technical-appendix">
      <h1>How We Worked It Out</h1>
      <p className="intro">
        This page shows the technical calculations behind your savings estimate. All values are for
        Year 1 only, based on today's energy prices.
      </p>

      {/* A) LEDGER TABLE */}
      <section className="ledger-section">
        <h2>Energy & Cost Breakdown</h2>
        <table className="ledger-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Current System</th>
              <th>{option.label}</th>
              <th>Difference</th>
            </tr>
          </thead>
          <tbody>
            {/* Delivered demand rows */}
            <tr>
              <td>Delivered space heat (kWh/yr)</td>
              <td>{formatNumber(baseline.spaceHeatKwh)}</td>
              <td>{formatNumber(option.detail.spaceHeatKwh)}</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Delivered hot water (kWh/yr)</td>
              <td>{formatNumber(baseline.dhwKwh)}</td>
              <td>{formatNumber(option.detail.dhwKwh)}</td>
              <td>—</td>
            </tr>
            <tr className="subtotal">
              <td>
                <strong>Delivered total (kWh/yr)</strong>
              </td>
              <td>
                <strong>{formatNumber(baseline.totalHeatKwh)}</strong>
              </td>
              <td>
                <strong>{formatNumber(option.detail.totalHeatKwh)}</strong>
              </td>
              <td>—</td>
            </tr>
            
            {/* Input energy rows (energy used to produce heating) */}
            <tr>
              <td>Energy used for space heating (kWh/yr)</td>
              <td>{formatNumber(baselineInputSpace)}</td>
              <td>{formatNumber(optionInputSpace)}</td>
              <td>{formatDifference(spaceDifference)}</td>
            </tr>
            <tr>
              <td>Energy used for hot water (kWh/yr)</td>
              <td>{formatNumber(baselineInputHotWater)}</td>
              <td>{formatNumber(optionInputHotWater)}</td>
              <td>{formatDifference(hotWaterDifference)}</td>
            </tr>
            <tr className="subtotal">
              <td>
                <strong>Total energy used (kWh/yr)</strong>
              </td>
              <td>
                <strong>{formatNumber(baselineInputTotal)}</strong>
              </td>
              <td>
                <strong>{formatNumber(optionInputTotal)}</strong>
              </td>
              <td className={totalDifference > 0 ? "savings" : "increase"}>
                <strong>{formatDifference(totalDifference)}</strong>
              </td>
            </tr>
            
            {/* System losses breakdown */}
            {(baseline.lossesBreakdown || option.detail.lossesBreakdown) && (
              <>
                <tr>
                  <td colSpan={4} style={{ paddingTop: '12px', fontStyle: 'italic', color: '#64748b', fontSize: '0.9em' }}>
                    System losses & inefficiencies:
                  </td>
                </tr>
                {(baseline.lossesBreakdown?.cyclingAndControlsKwh || option.detail.lossesBreakdown?.cyclingAndControlsKwh) && (
                  <tr className="loss-row">
                    <td style={{ paddingLeft: '24px' }}>Cycling / oversizing / control behavior</td>
                    <td>{baseline.lossesBreakdown?.cyclingAndControlsKwh ? '+' + formatNumber(baseline.lossesBreakdown.cyclingAndControlsKwh) + ' kWh/yr' : '—'}</td>
                    <td>{option.detail.lossesBreakdown?.cyclingAndControlsKwh ? '+' + formatNumber(option.detail.lossesBreakdown.cyclingAndControlsKwh) + ' kWh/yr' : '—'}</td>
                    <td>—</td>
                  </tr>
                )}
                {(baseline.lossesBreakdown?.distributionKwh || option.detail.lossesBreakdown?.distributionKwh) && (
                  <tr className="loss-row">
                    <td style={{ paddingLeft: '24px' }}>Pipework & distribution losses</td>
                    <td>{baseline.lossesBreakdown?.distributionKwh ? '+' + formatNumber(baseline.lossesBreakdown.distributionKwh) + ' kWh/yr' : '—'}</td>
                    <td>{option.detail.lossesBreakdown?.distributionKwh ? '+' + formatNumber(option.detail.lossesBreakdown.distributionKwh) + ' kWh/yr' : '—'}</td>
                    <td>—</td>
                  </tr>
                )}
                {((baseline.lossesBreakdown?.cylinderStandingKwh || option.detail.lossesBreakdown?.cylinderStandingKwh)
                  && !optionIsCombiArchitecture) && (
                  <tr className="loss-row">
                    <td style={{ paddingLeft: '24px' }}>Cylinder standing losses</td>
                    <td>{baseline.lossesBreakdown?.cylinderStandingKwh ? '+' + formatNumber(baseline.lossesBreakdown.cylinderStandingKwh) + ' kWh/yr' : '—'}</td>
                    <td>{option.detail.lossesBreakdown?.cylinderStandingKwh ? '+' + formatNumber(option.detail.lossesBreakdown.cylinderStandingKwh) + ' kWh/yr' : '—'}</td>
                    <td>—</td>
                  </tr>
                )}
                {(baseline.lossesBreakdown?.keepWarmKwh || option.detail.lossesBreakdown?.keepWarmKwh) && (
                  <tr className="loss-row">
                    <td style={{ paddingLeft: '24px' }}>Keep-warm / preheat</td>
                    <td>{baseline.lossesBreakdown?.keepWarmKwh ? '+' + formatNumber(baseline.lossesBreakdown.keepWarmKwh) + ' kWh/yr' : '—'}</td>
                    <td>{option.detail.lossesBreakdown?.keepWarmKwh ? '+' + formatNumber(option.detail.lossesBreakdown.keepWarmKwh) + ' kWh/yr' : '—'}</td>
                    <td>—</td>
                  </tr>
                )}
                <tr className="subtotal">
                  <td>
                    <strong>Total system losses</strong>
                  </td>
                  <td>
                    <strong>{baseline.totalLossesKwh ? '+' + formatNumber(baseline.totalLossesKwh) + ' kWh/yr' : '—'}</strong>
                  </td>
                  <td>
                    <strong>{option.detail.totalLossesKwh ? '+' + formatNumber(option.detail.totalLossesKwh) + ' kWh/yr' : '—'}</strong>
                  </td>
                  <td>—</td>
                </tr>
                <tr className="subtotal">
                  <td>
                    <strong>Fuel input required</strong>
                  </td>
                  <td>
                    <strong>{baseline.fuelInputKwh ? formatNumber(baseline.fuelInputKwh) + ' kWh/yr' : '—'}</strong>
                  </td>
                  <td>
                    <strong>{option.detail.fuelInputKwh ? formatNumber(option.detail.fuelInputKwh) + ' kWh/yr' : '—'}</strong>
                  </td>
                  <td>—</td>
                </tr>
              </>
            )}
            
            <tr>
              <td>System performance</td>
              <td>{baseline.performance.label}</td>
              <td>{option.detail.performance.label}</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Gas consumption</td>
              <td>{baseline.fuelKwh.gas ? formatNumber(baseline.fuelKwh.gas) + ' kWh/yr' : '—'}</td>
              <td>
                {option.detail.fuelKwh.gas
                  ? formatNumber(option.detail.fuelKwh.gas) + ' kWh/yr'
                  : '—'}
              </td>
              <td>
                {baseline.fuelKwh.gas && option.detail.fuelKwh.gas
                  ? formatNumber(baseline.fuelKwh.gas - option.detail.fuelKwh.gas) + ' kWh/yr'
                  : '—'}
              </td>
            </tr>
            <tr>
              <td>Electricity consumption</td>
              <td>
                {baseline.fuelKwh.electricity
                  ? formatNumber(baseline.fuelKwh.electricity) + ' kWh/yr'
                  : '—'}
              </td>
              <td>
                {option.detail.fuelKwh.electricity
                  ? formatNumber(option.detail.fuelKwh.electricity) + ' kWh/yr'
                  : '—'}
              </td>
              <td>
                {baseline.fuelKwh.electricity && option.detail.fuelKwh.electricity
                  ? formatNumber(
                      baseline.fuelKwh.electricity - option.detail.fuelKwh.electricity
                    ) + ' kWh/yr'
                  : option.detail.fuelKwh.electricity
                  ? '+' + formatNumber(option.detail.fuelKwh.electricity) + ' kWh/yr'
                  : '—'}
              </td>
            </tr>
            <tr className="total-row">
              <td>
                <strong>Annual running cost</strong>
              </td>
              <td>
                <strong>{formatCurrency(baseline.costGbp)}/yr</strong>
              </td>
              <td>
                <strong>{formatCurrency(option.detail.costGbp)}/yr</strong>
              </td>
              <td className="savings">
                <strong>{formatCurrency(baseline.costGbp - option.detail.costGbp)} saving</strong>
              </td>
            </tr>
            <tr className="total-row">
              <td>
                <strong>Annual carbon emissions</strong>
              </td>
              <td>
                <strong>{formatNumber(baseline.carbonKg)} kgCO₂/yr</strong>
              </td>
              <td>
                <strong>{formatNumber(option.detail.carbonKg)} kgCO₂/yr</strong>
              </td>
              <td className="savings">
                <strong>{formatNumber(baseline.carbonKg - option.detail.carbonKg)} kgCO₂ saving</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* CARBON AUDIT TABLE */}
      <section className="carbon-audit-section">
        <h2>Carbon Calculation Audit</h2>
        <p className="section-intro">
          This table shows how carbon emissions are calculated from fuel consumption using carbon intensity factors.
        </p>
        <table className="ledger-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Calculation Path</th>
              <th>Current System</th>
              <th>{option.label}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Gas Carbon</td>
              <td>(Fuel kWh) × {formatDecimal(assumptions.carbonFactorGas, 3)}</td>
              <td>
                {baseline.fuelKwh.gas
                  ? `${formatNumber(baseline.fuelKwh.gas)} × ${formatDecimal(assumptions.carbonFactorGas, 3)} = ${formatNumber(baseline.fuelKwh.gas * assumptions.carbonFactorGas)} kg`
                  : '—'}
              </td>
              <td>
                {option.detail.fuelKwh.gas
                  ? `${formatNumber(option.detail.fuelKwh.gas)} × ${formatDecimal(assumptions.carbonFactorGas, 3)} = ${formatNumber(option.detail.fuelKwh.gas * assumptions.carbonFactorGas)} kg`
                  : '—'}
              </td>
            </tr>
            <tr>
              <td>Electricity Carbon</td>
              <td>(Fuel kWh) × {formatDecimal(assumptions.carbonFactorElectricity, 3)}</td>
              <td>
                {baseline.fuelKwh.electricity
                  ? `${formatNumber(baseline.fuelKwh.electricity)} × ${formatDecimal(assumptions.carbonFactorElectricity, 3)} = ${formatNumber(baseline.fuelKwh.electricity * assumptions.carbonFactorElectricity)} kg`
                  : '—'}
              </td>
              <td>
                {option.detail.fuelKwh.electricity
                  ? `${formatNumber(option.detail.fuelKwh.electricity)} × ${formatDecimal(assumptions.carbonFactorElectricity, 3)} = ${formatNumber(option.detail.fuelKwh.electricity * assumptions.carbonFactorElectricity)} kg`
                  : '—'}
              </td>
            </tr>
            <tr className="total-row">
              <td>
                <strong>Total Carbon</strong>
              </td>
              <td>
                <strong>Gas + Electricity</strong>
              </td>
              <td>
                <strong>{formatNumber(baseline.carbonKg)} kg</strong>
              </td>
              <td>
                <strong>{formatNumber(option.detail.carbonKg)} kg</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ENERGY SAVINGS GRAPH */}
      <section className="energy-savings-graph-section">
        <h2>Energy & Carbon Comparison</h2>
        <p className="section-intro">
          Visual comparison of energy consumption and carbon emissions between your current system and the recommended option.
        </p>
        
        <div className="chart-container">
          <h3>Annual Energy Consumption</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                {
                  name: 'Current System',
                  Gas: baseline.fuelKwh.gas || 0,
                  Electricity: baseline.fuelKwh.electricity || 0,
                },
                {
                  name: option.label,
                  Gas: option.detail.fuelKwh.gas || 0,
                  Electricity: option.detail.fuelKwh.electricity || 0,
                },
              ]}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis label={{ value: 'Energy (kWh/year)', angle: -90, position: 'insideLeft' }} />
              <Tooltip formatter={(value) => `${formatNumber(Number(value))} kWh`} />
              <Legend />
              <Bar dataKey="Gas" fill="#FF6B35" name="Gas" />
              <Bar dataKey="Electricity" fill="#4ECDC4" name="Electricity" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>Annual Carbon Emissions</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                {
                  name: 'Current System',
                  'Carbon Emissions': baseline.carbonKg,
                },
                {
                  name: option.label,
                  'Carbon Emissions': option.detail.carbonKg,
                },
              ]}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis label={{ value: 'Carbon (kgCO₂/year)', angle: -90, position: 'insideLeft' }} />
              <Tooltip formatter={(value) => `${formatNumber(Number(value))} kg`} />
              <Legend />
              <Bar dataKey="Carbon Emissions" fill="#9B59B6" name="Carbon Emissions (kgCO₂)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="savings-summary">
          <div className="savings-card">
            <div className="savings-label">Energy Savings</div>
            <div className="savings-value">
              {formatNumber(
                (baseline.fuelKwh.gas || 0) + (baseline.fuelKwh.electricity || 0) -
                ((option.detail.fuelKwh.gas || 0) + (option.detail.fuelKwh.electricity || 0))
              )} kWh/year
            </div>
          </div>
          <div className="savings-card">
            <div className="savings-label">Carbon Savings</div>
            <div className="savings-value">
              {formatNumber(baseline.carbonKg - option.detail.carbonKg)} kgCO₂/year
            </div>
          </div>
          <div className="savings-card">
            <div className="savings-label">Cost Savings</div>
            <div className="savings-value">
              {formatCurrency(baseline.costGbp - option.detail.costGbp)}/year
            </div>
          </div>
        </div>
      </section>

      {/* B) ASSUMPTIONS BOX */}
      <section className="assumptions-section">
        <h2>Assumptions Used</h2>
        <div className="assumptions-grid">
          <div className="assumption-item">
            <div className="assumption-label">Gas tariff</div>
            <div className="assumption-value">{formatDecimal(assumptions.gasTariffPPerKwh, 2)}p/kWh</div>
          </div>
          <div className="assumption-item">
            <div className="assumption-label">Electricity tariff</div>
            <div className="assumption-value">
              {formatDecimal(assumptions.electricityTariffPPerKwh, 2)}p/kWh
            </div>
          </div>
          <div className="assumption-item">
            <div className="assumption-label">Carbon factor (gas)</div>
            <div className="assumption-value">
              {formatDecimal(assumptions.carbonFactorGas, 3)} kgCO₂/kWh
            </div>
          </div>
          <div className="assumption-item">
            <div className="assumption-label">Carbon factor (electricity)</div>
            <div className="assumption-value">
              {formatDecimal(assumptions.carbonFactorElectricity, 3)} kgCO₂/kWh
            </div>
          </div>
          <div className="assumption-item assumption-full">
            <div className="assumption-label">Heat demand calculation</div>
            <div className="assumption-value">{assumptions.heatLossSource}</div>
          </div>
          <div className="assumption-item assumption-full">
            <div className="assumption-label">System performance</div>
            <div className="assumption-value">{assumptions.performanceSource}</div>
          </div>
        </div>
      </section>

      {/* B2) THERMODYNAMIC ASSUMPTIONS */}
      <DevOnly isDebugEnabled={isDebugEnabled}>
        <section className="thermodynamic-section">
          <h2>Thermodynamic Assumptions & Constants</h2>
          <div className="thermodynamic-content">
            <p className="section-intro">
              The following constants and assumptions form the foundation of our energy calculations. These values are based on industry standards and regulatory frameworks.
            </p>
            
            <div className="assumptions-list">
              <div className="assumption-block">
                <h3>Energy Density</h3>
                <ul>
                  <li><strong>Natural Gas:</strong> 10.35 kWh/m³ (volumetric), 11.0 kWh/kg (gravimetric)</li>
                  <li><strong>Grid Electricity:</strong> 1 kWh per kWh (by definition)</li>
                </ul>
              </div>
              
              <div className="assumption-block">
                <h3>System Efficiency Standards</h3>
                <ul>
                  <li><strong>ErP Efficiency (Modern Condensing Boilers):</strong> 0.92 (92% manufacturer standard)</li>
                  <li><strong>SEDBUK Efficiency (Legacy Systems):</strong> Varies by age, typically 0.60-0.85</li>
                  <li><strong>Heat Pump COP:</strong> Coefficient of Performance based on system design temperature</li>
                </ul>
              </div>
              
              <div className="assumption-block">
                <h3>Performance Degradation Model</h3>
                <p>
                  Annual efficiency degradation follows the formula:
                </p>
                <div className="formula">
                  η(t) = η₀ × (1 - d)ᵗ
                </div>
                <p className="formula-description">
                  Where:
                </p>
                <ul>
                  <li><strong>η(t)</strong> = Efficiency at year t</li>
                  <li><strong>η₀</strong> = Design efficiency (manufacturer rated)</li>
                  <li><strong>d</strong> = Annual degradation coefficient (typically 0.01 or 1.0% per year)</li>
                  <li><strong>t</strong> = Age in years</li>
                </ul>
              </div>
              
              <div className="assumption-block">
                <h3>Carbon Intensity Factors</h3>
                <ul>
                  <li><strong>Natural Gas:</strong> {formatDecimal(assumptions.carbonFactorGas, 3)} kgCO₂/kWh (SAP 10.2 standard)</li>
                  <li><strong>Grid Electricity:</strong> {formatDecimal(assumptions.carbonFactorElectricity, 3)} kgCO₂/kWh (UK Grid average, varies by time of use)</li>
                </ul>
              </div>
              
              <div className="assumption-block">
                <h3>Heat Demand Calculation Methodology</h3>
                <p>
                  Heat demand is calculated using building physics principles:
                </p>
                <ul>
                  <li><strong>Space Heating:</strong> Based on fabric heat loss, ventilation loss, and internal gains</li>
                  <li><strong>Hot Water:</strong> Based on occupancy, usage patterns, and cylinder losses</li>
                  <li><strong>Seasonal Variation:</strong> Degree-day methodology applied for space heating</li>
                </ul>
              </div>
            </div>
            
            <div className="methodology-note">
              <h3>Model Confidence & Uncertainty</h3>
              <p>
                Our calculations use well-established engineering principles backed by UK Building Regulations 
                (Part L), SAP methodology, and manufacturer data. Uncertainty ranges account for:
              </p>
              <ul>
                <li>Variations in occupant behavior and usage patterns</li>
                <li>Weather variations from typical meteorological year data</li>
                <li>Installation quality and system commissioning</li>
                <li>Building fabric variations from design specifications</li>
              </ul>
              <p>
                The shaded confidence bands on charts represent ±15% typical variation based on these factors.
              </p>
            </div>
          </div>
        </section>
      </DevOnly>

      {/* C) CONFIDENCE BADGE */}
      <section className="confidence-section">
        <div className={`confidence-badge confidence-${confidence.level}`}>
          <div className="confidence-level">
            Estimate confidence: <strong>{confidence.level.toUpperCase()}</strong>
          </div>
          <div className="confidence-reason">{confidence.reason}</div>
        </div>
      </section>

      {/* D) FULL DISCLAIMER */}
      <section className="disclaimer-section">
        <h2>Important Information</h2>
        <div className="disclaimer-content">
          <p>
            <strong>This is an estimate only.</strong> Actual savings will vary based on your usage
            patterns, property characteristics, weather conditions, and energy price changes.
          </p>
          <p>
            <strong>Energy prices:</strong> Calculations use current energy prices as of today. Future
            price changes (up or down) will affect your actual savings.
          </p>
          <p>
            <strong>Installation costs not included:</strong> This estimate shows running cost savings
            only. Contact an installer for installation quotes and payback calculations.
          </p>
          <p>
            <strong>Year 1 only:</strong> These figures represent typical first-year performance. System
            efficiency may change over time due to maintenance, wear, or energy price fluctuations.
          </p>
          <p>
            <strong>Professional advice recommended:</strong> This tool provides guidance only. For
            detailed recommendations, consult a qualified heating engineer who can assess your specific
            property and needs.
          </p>
        </div>
      </section>

      <style>{`
        .how-we-worked-it-out {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial,
            sans-serif;
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem;
          line-height: 1.6;
          color: #2d3748;
        }

        h1 {
          font-size: 2rem;
          margin-bottom: 1rem;
          color: #1a202c;
        }

        .intro {
          font-size: 1.125rem;
          color: #4a5568;
          margin-bottom: 2rem;
        }

        section {
          margin-bottom: 3rem;
        }

        h2 {
          font-size: 1.5rem;
          margin-bottom: 1rem;
          color: #2d3748;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 0.5rem;
        }

        /* LEDGER TABLE */
        .ledger-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
          overflow: hidden;
        }

        .ledger-table th {
          background: #2d3748;
          color: white;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
        }

        .ledger-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .ledger-table tr:last-child td {
          border-bottom: none;
        }

        .ledger-table tr.subtotal td {
          background: #f7fafc;
          font-weight: 600;
        }

        .ledger-table tr.total-row td {
          background: #edf2f7;
          font-weight: 700;
          font-size: 1.05rem;
          padding: 1rem;
        }

        .ledger-table td.savings {
          color: #38a169;
        }

        /* ASSUMPTIONS GRID */
        .assumptions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .assumption-item {
          background: #f7fafc;
          padding: 1rem;
          border-radius: 8px;
          border-left: 4px solid #4299e1;
        }

        .assumption-item.assumption-full {
          grid-column: 1 / -1;
        }

        .assumption-label {
          font-size: 0.875rem;
          color: #718096;
          margin-bottom: 0.25rem;
          font-weight: 600;
        }

        .assumption-value {
          font-size: 1rem;
          color: #2d3748;
          font-weight: 500;
        }

        /* CONFIDENCE BADGE */
        .confidence-badge {
          padding: 1.5rem;
          border-radius: 8px;
          border-left: 6px solid;
        }

        .confidence-badge.confidence-high {
          background: #f0fff4;
          border-color: #38a169;
        }

        .confidence-badge.confidence-medium {
          background: #fffaf0;
          border-color: #dd6b20;
        }

        .confidence-badge.confidence-low {
          background: #fff5f5;
          border-color: #e53e3e;
        }

        .confidence-level {
          font-size: 1.125rem;
          margin-bottom: 0.5rem;
        }

        .confidence-reason {
          color: #4a5568;
        }

        /* DISCLAIMER */
        .disclaimer-content {
          background: #edf2f7;
          padding: 1.5rem;
          border-radius: 8px;
          font-size: 0.9375rem;
          line-height: 1.7;
        }

        .disclaimer-content p {
          margin-bottom: 1rem;
        }

        .disclaimer-content p:last-child {
          margin-bottom: 0;
        }

        /* THERMODYNAMIC ASSUMPTIONS SECTION */
        .thermodynamic-section {
          margin-top: 3rem;
          padding: 1.5rem;
          background: #f7fafc;
          border-radius: 8px;
          border: 2px solid #4299e1;
        }

        .thermodynamic-content .section-intro {
          font-size: 1rem;
          color: #2d3748;
          margin-bottom: 1.5rem;
          font-style: italic;
        }

        .assumptions-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .assumption-block {
          background: white;
          padding: 1rem;
          border-radius: 6px;
          border-left: 4px solid #4299e1;
        }

        .assumption-block h3 {
          font-size: 1.125rem;
          color: #2d3748;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .assumption-block ul {
          list-style: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .assumption-block li {
          margin-bottom: 0.5rem;
          color: #4a5568;
          line-height: 1.6;
        }

        .assumption-block p {
          color: #4a5568;
          line-height: 1.7;
          margin: 0.5rem 0;
        }

        .formula {
          font-family: 'Georgia', 'Times New Roman', serif;
          font-size: 1.125rem;
          color: #2d3748;
          background: #edf2f7;
          padding: 1rem;
          border-radius: 4px;
          text-align: center;
          margin: 1rem 0;
          font-weight: 500;
          letter-spacing: 0.5px;
        }

        .formula-description {
          font-size: 0.9375rem;
          color: #4a5568;
          margin-top: 0.5rem;
          margin-bottom: 0.25rem;
        }

        .methodology-note {
          margin-top: 1.5rem;
          padding: 1.25rem;
          background: #fffaf0;
          border-radius: 6px;
          border-left: 4px solid #dd6b20;
        }

        .methodology-note h3 {
          font-size: 1.125rem;
          color: #2d3748;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .methodology-note p {
          color: #4a5568;
          line-height: 1.7;
          margin-bottom: 0.75rem;
        }

        .methodology-note ul {
          list-style: disc;
          padding-left: 1.5rem;
          margin: 0.75rem 0;
        }

        .methodology-note li {
          margin-bottom: 0.5rem;
          color: #4a5568;
          line-height: 1.6;
        }

        /* CARBON AUDIT SECTION */
        .carbon-audit-section {
          margin-bottom: 3rem;
        }

        .carbon-audit-section .section-intro {
          font-size: 0.9375rem;
          color: #4a5568;
          margin-bottom: 1rem;
          font-style: italic;
        }

        /* ENERGY SAVINGS GRAPH SECTION */
        .energy-savings-graph-section {
          margin-bottom: 3rem;
        }

        .energy-savings-graph-section .section-intro {
          font-size: 0.9375rem;
          color: #4a5568;
          margin-bottom: 1.5rem;
          font-style: italic;
        }

        .chart-container {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .chart-container h3 {
          font-size: 1.125rem;
          color: #2d3748;
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .savings-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .savings-card {
          background: white;
          color: #1e293b;
          padding: 1.5rem;
          border-radius: 8px;
          border: 2px solid #e2e8f0;
          border-left: 4px solid #2563eb;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        .savings-label {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .savings-value {
          font-size: 1.5rem;
          font-weight: 700;
        }

        @media (max-width: 768px) {
          .how-we-worked-it-out {
            padding: 1rem;
          }

          .ledger-table {
            font-size: 0.875rem;
          }

          .ledger-table th,
          .ledger-table td {
            padding: 0.5rem;
          }

          .assumptions-grid {
            grid-template-columns: 1fr;
          }
        }

        @media print { 
          .technical-appendix { 
            page-break-before: always; 
          } 
        }
      `}</style>
    </div>
  );
}

export default HowWeWorkedItOut;
